/**
 * Los enlaces de un solo uso: crear la contraseña con la invitación, y pedir uno
 * nuevo cuando se olvidó.
 *
 * Va aparte del repositorio del jugador porque no hay sesión de por medio: lo
 * que autoriza es el enlace (ver `rutas-credenciales.ts` del API). Sin API, la
 * demostración no tiene cuentas de verdad y lo dice.
 */

/**
 * A dónde va la persona al terminar. El que organiza va al panel: entrar al
 * site del jugador con una cuenta que no juega lo dejaría en una pantalla vacía.
 */
export type DestinoDeClave = { destino: 'site' } | { destino: 'panel'; urlPanel: string }

export type EnlaceDeClave = {
  tipo: 'definir_password' | 'reset_password'
  nombre: string
  usuario: string
} & DestinoDeClave

const SIN_API = 'En la demostración no hay cuentas de verdad: no hay contraseña que crear.'

async function pedir<T>(ruta: string, cuerpo?: unknown): Promise<T> {
  const url = import.meta.env.VITE_API_URL
  if (!url) throw new Error(SIN_API)

  let respuesta: Response
  try {
    respuesta = await fetch(`${url.replace(/\/+$/, '')}${ruta}`, {
      ...(cuerpo !== undefined
        ? { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(cuerpo) }
        : {}),
      signal: AbortSignal.timeout(15_000),
    })
  } catch {
    throw new Error('No se pudo conectar. Prueba de nuevo cuando tengas señal.')
  }

  const datos = (await respuesta.json().catch(() => ({}))) as { mensaje?: string }
  if (!respuesta.ok) throw new Error(datos.mensaje ?? 'Algo salió mal. Prueba de nuevo.')
  return datos as T
}

/** Lo que dice el enlace, o un error con el motivo si ya no sirve. */
export function leerEnlace(token: string): Promise<EnlaceDeClave> {
  return pedir(`/credenciales/${encodeURIComponent(token)}`)
}

/** Define la contraseña. Devuelve el usuario con el que ya puede entrar, y a dónde. */
export function definirClave(
  token: string,
  password: string,
): Promise<{ usuario: string } & DestinoDeClave> {
  return pedir(`/credenciales/${encodeURIComponent(token)}`, { password })
}

/** Pide un enlace nuevo. El mensaje es el mismo exista o no la cuenta. */
export async function olvideMiClave(usuario: string): Promise<string> {
  const { mensaje } = await pedir<{ mensaje: string }>('/credenciales/olvido', { usuario })
  return mensaje
}
